import { useEffect, useRef } from 'react';

/**
 * Restaura a posição de rolagem quando `ready` fica true e continua salvando
 * a posição em sessionStorage. Isso evita restaurar antes do conteúdo carregar
 * (o que faria a página ficar no topo).
 *
 * Uso:
 *   useScrollRestoration('leads', !loading);
 */
export default function useScrollRestoration(key, ready = true) {
  const storageKey = `scroll:${key}`;
  const restored = useRef(false);

  // Salvar posição continuamente (throttle via rAF)
  useEffect(() => {
    let ticking = false;
    const save = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        sessionStorage.setItem(storageKey, String(window.scrollY));
        ticking = false;
      });
    };
    const onUnload = () => {
      sessionStorage.setItem(storageKey, String(window.scrollY));
    };
    window.addEventListener('scroll', save, { passive: true });
    window.addEventListener('beforeunload', onUnload);
    return () => {
      window.removeEventListener('scroll', save);
      window.removeEventListener('beforeunload', onUnload);
    };
  }, [storageKey]);

  // Restaurar SOMENTE quando o conteúdo estiver pronto
  useEffect(() => {
    if (!ready || restored.current) return;
    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      const y = parseInt(saved, 10);
      if (!Number.isNaN(y) && y > 0) {
        // 2 rAFs para garantir que o layout foi finalizado
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            window.scrollTo({ top: y, behavior: 'auto' });
          });
        });
      }
    }
    restored.current = true;
  }, [ready, storageKey]);

  // Se a key mudar (troca de aba), permite restaurar de novo
  useEffect(() => {
    restored.current = false;
  }, [storageKey]);
}
